import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { GdprConsentService } from '../../../service/gdpr-consent.service';
import { JwtHelperService } from '../../../service/jwt-helper.service';
import * as GdprActions from '../actions/gdpr.actions';
import * as AuthActions from '../../auth/actions/auth.actions';

@Injectable()
export class GdprEffects {
  private actions$ = inject(Actions);
  private gdprService = inject(GdprConsentService);
  private jwtHelper = inject(JwtHelperService);

  /**
   * Load GDPR consent status on login or app rehydration. The backend
   * /admin/gdpr/consent/status endpoint is SUPER_ADMIN-only — only fire
   * the load for users with that role to avoid 403/500 errors for
   * ROLE_ADMIN or ROLE_USER on every login.
   */
  loadOnAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess, AuthActions.checkAuthSuccess),
      filter(
        (action) =>
          !!action.token?.accessToken &&
          this.jwtHelper.hasRole(action.token.accessToken, 'ROLE_SUPER_ADMIN'),
      ),
      map(() => GdprActions.loadGdprStatus()),
    ),
  );

  /**
   * Fetch GDPR consent status from backend.
   */
  loadGdprStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GdprActions.loadGdprStatus),
      switchMap(() =>
        this.gdprService.getConsentStatus().pipe(
          map((status) => GdprActions.loadGdprStatusSuccess({ status })),
          catchError((error) =>
            of(
              GdprActions.loadGdprStatusFailure({
                error: error?.message || 'Failed to load GDPR consent status',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Record GDPR consent acceptance via the API.
   * On success the reducer flips state.consented to true so the route guard
   * and any selector consumers (menu, settings) immediately reflect the new
   * state — no manual refetch needed.
   */
  acceptGdprConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GdprActions.acceptGdprConsent),
      switchMap(() =>
        this.gdprService.acceptConsent().pipe(
          map((record) => GdprActions.acceptGdprConsentSuccess({ record })),
          catchError((error) =>
            of(
              GdprActions.acceptGdprConsentFailure({
                error: error?.message || 'Failed to record GDPR consent',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Clear GDPR status on logout.
   */
  clearOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSuccess),
      map(() => GdprActions.clearGdprStatus()),
    ),
  );
}
